/**
 * Date Formatting Utilities
 * Supports company-customizable date patterns:
 * - 'DD-MM-YYYY' (Default, e.g. 17-09-2026)
 * - 'DD/MM/YYYY' (e.g. 17/09/2026)
 * - 'YYYY-MM-DD' (e.g. 2026-09-17)
 * - 'DD MMM YYYY' (e.g. 17 Sep 2026)
 */

export const DATE_FORMAT_OPTIONS = [
  { id: "DD-MM-YYYY", label: "DD-MM-YYYY (e.g. 17-09-2026)" },
  { id: "DD/MM/YYYY", label: "DD/MM/YYYY (e.g. 17/09/2026)" },
  { id: "YYYY-MM-DD", label: "YYYY-MM-DD (e.g. 2026-09-17)" },
  { id: "DD MMM YYYY", label: "DD MMM YYYY (e.g. 17 Sep 2026)" }
];

export const DEFAULT_DATE_FORMAT = "DD-MM-YYYY";

/**
 * Parses any date input (YYYY-MM-DD, DD-MM-YYYY, DD/MM/YYYY, ISO string, Firestore Timestamp, Date).
 * Returns a Date, or null when it can't be read.
 */
export function parseAppDate(dateInput) {
  if (!dateInput) return null;

  let d;
  if (typeof dateInput === "string") {
    const trimmed = dateInput.trim();
    // YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      const [year, month, day] = trimmed.split("-").map(Number);
      d = new Date(year, month - 1, day);
    }
    // DD-MM-YYYY
    else if (/^\d{2}-\d{2}-\d{4}$/.test(trimmed)) {
      const [day, month, year] = trimmed.split("-").map(Number);
      d = new Date(year, month - 1, day);
    }
    // DD/MM/YYYY
    else if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
      const [day, month, year] = trimmed.split("/").map(Number);
      d = new Date(year, month - 1, day);
    }
    // General ISO / other date strings
    else {
      d = new Date(trimmed);
    }
  } else if (dateInput && typeof dateInput.toDate === "function") {
    d = dateInput.toDate();
  } else if (dateInput && dateInput.seconds !== undefined) {
    d = new Date(dateInput.seconds * 1000);
  } else if (dateInput instanceof Date) {
    d = dateInput;
  } else {
    d = new Date(dateInput);
  }

  return d && !isNaN(d.getTime()) ? d : null;
}

/** Sortable number for a date input (null when missing/unreadable). */
export function dateSortValue(dateInput) {
  return parseAppDate(dateInput)?.getTime() ?? null;
}

/**
 * Formats any date input (YYYY-MM-DD string, ISO string, Firestore Timestamp, Date object)
 * according to the company's date pattern.
 */
export function formatAppDate(dateInput, pattern = DEFAULT_DATE_FORMAT) {
  if (!dateInput) return "";
  const d = parseAppDate(dateInput);
  if (!d) {
    return String(dateInput);
  }

  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = String(d.getFullYear());

  const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const mmm = MONTHS[d.getMonth()];

  const targetPattern = pattern || DEFAULT_DATE_FORMAT;

  switch (targetPattern) {
    case "DD/MM/YYYY":
      return `${day}/${month}/${year}`;
    case "YYYY-MM-DD":
      return `${year}-${month}-${day}`;
    case "DD MMM YYYY":
      return `${day} ${mmm} ${year}`;
    case "DD-MM-YYYY":
    default:
      return `${day}-${month}-${year}`;
  }
}
